/**
 * Server state.
 *
 * The whole budget is one query. `/snapshot` returns everything, the objects are
 * densely cross-referenced (a transaction's envelope balance depends on other
 * transactions), and partial caches of that graph go stale in ways that are hard
 * to see. One key, invalidated on every write and on every realtime nudge.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Snapshot } from '@alohamula/domain';
import * as api from './client';

export const snapshotKey = ['snapshot'] as const;
export const trashKey = ['trash'] as const;
export const assistantKey = ['assistant'] as const;
export const presenceKey = ['presence'] as const;

export function useSnapshot() {
  return useQuery({
    queryKey: snapshotKey,
    queryFn: ({ signal }) => api.fetchSnapshot(signal),
    // The scraper can add transactions at any time and the socket tells us when.
    staleTime: 30_000,
  });
}

export function refreshSnapshot(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: snapshotKey }).then(() => undefined);
}

export function useTrashedTransactions(enabled: boolean) {
  return useQuery({
    queryKey: trashKey,
    queryFn: () => api.fetchTrashedTransactions(),
    enabled,
  });
}

/**
 * Wraps a mutation so success refetches the snapshot.
 *
 * Deliberately not optimistic. Every write returns the new server version, and
 * guessing the resulting balances client-side would show numbers that disagree
 * with the iOS app for a moment. Refetching one small document is fast enough
 * that the honest version wins.
 */
function useSnapshotMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  options: { alsoInvalidateTrash?: boolean } = {}
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: snapshotKey });
      if (options.alsoInvalidateTrash) {
        await client.invalidateQueries({ queryKey: trashKey });
      }
    },
  });
}

export function useSetAllocations() {
  return useSnapshotMutation(
    (args: {
      transactionId: string;
      allocations: api.AllocationInput[];
      expectedVersion?: number;
    }) => api.setAllocations(args.transactionId, args.allocations, args.expectedVersion)
  );
}

export function useTrashTransaction() {
  return useSnapshotMutation(
    (args: { id: string; expectedVersion: number; reason?: string }) =>
      api.trashTransaction(args.id, args.expectedVersion, args.reason),
    { alsoInvalidateTrash: true }
  );
}

export function useRestoreTransaction() {
  return useSnapshotMutation(
    (args: { id: string; expectedVersion: number }) =>
      api.restoreTransaction(args.id, args.expectedVersion),
    { alsoInvalidateTrash: true }
  );
}

export function useUpdateTransaction() {
  return useSnapshotMutation((args: { id: string; input: api.UpdateTransactionInput }) =>
    api.updateTransaction(args.id, args.input)
  );
}

export function useCreateBudgetItem() {
  return useSnapshotMutation((input: api.CreateBudgetItemInput) => api.createBudgetItem(input));
}

export function useUpdateBudgetItem() {
  return useSnapshotMutation((args: { id: string; input: api.UpdateBudgetItemInput }) =>
    api.updateBudgetItem(args.id, args.input)
  );
}

export function useDeleteBudgetItem() {
  return useSnapshotMutation((id: string) => api.deleteBudgetItem(id));
}

export function useUpdatePaycheckSettings() {
  return useSnapshotMutation((input: api.UpdatePaycheckSettingsInput) =>
    api.updatePaycheckSettings(input)
  );
}

export function useBorrowBetweenItems() {
  return useSnapshotMutation((input: Parameters<typeof api.borrowBetweenItems>[0]) =>
    api.borrowBetweenItems(input)
  );
}

export function useAssistantSuggestions() {
  return useQuery({
    queryKey: assistantKey,
    queryFn: () => api.listAssistantSuggestions(),
    staleTime: 60_000,
  });
}

export function useRunAnalyst() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.refreshAssistantWatch(),
    onSuccess: (data) => {
      client.setQueryData(assistantKey, data);
    },
  });
}

export function useDismissAssistantSuggestion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.dismissAssistantSuggestion(id),
    onSuccess: (data) => {
      client.setQueryData(assistantKey, data);
    },
  });
}

export function useCreateSortingRule() {
  return useSnapshotMutation(
    (input: { keyword: string; budgetItemId: string; createdFromDescription?: string | null }) =>
      api.createSortingRule(input)
  );
}

export function useUpdateSortingRule() {
  return useSnapshotMutation(
    (input: { id: string; keyword: string; budgetItemId: string }) =>
      api.updateSortingRule(input.id, {
        keyword: input.keyword,
        budgetItemId: input.budgetItemId,
      })
  );
}

export function useDeleteSortingRule() {
  return useSnapshotMutation((id: string) => api.deleteSortingRule(id));
}

export function usePresence() {
  return useQuery({
    queryKey: presenceKey,
    queryFn: ({ signal }) => api.fetchPresence(signal),
    // Presence moves while people are out; refresh while the map is open.
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export type { Snapshot };
